// SSO-aware click handler for CoE lab cards. Renders a card that,
// when clicked by an authenticated student, calls
// POST /api/sso/issue-handoff and then does a **same-tab** redirect to
// `<partner_url>?sso=<jwt>` so the student lands already logged in on
// TechCatalyst / CIDSA — no `about:blank` intermediate tab (which
// looked broken on Capacitor / mobile), no popup blocker to fight.
//
// A short 250 ms delay is intentional after the JWT arrives — gives
// the click a perceived tactile pause and lets the browser paint any
// pressed-state before navigating away.
//
// If the user is NOT logged in, we push them to /login?sso_lab=<code>
// so we capture the lead and continue the handoff after login.

import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../api";
import { useAuth } from "../context/AuthContext";
import { getCoeExternalUrl } from "../lib/coeExternalUrls";

const HANDOFF_DELAY_MS = 250;

export default function CoeLabCardLink({
  labCode,
  fallbackInternalHref,
  className,
  testid,
  externalTestidAttr = true,
  children,
}) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const extUrl = getCoeExternalUrl(labCode);

  // No partner URL for this code — use the internal detail route.
  if (!extUrl) {
    return (
      <a
        href={fallbackInternalHref}
        className={className}
        data-testid={testid}
        onClick={(e) => { e.preventDefault(); navigate(fallbackInternalHref); }}
      >
        {children}
      </a>
    );
  }

  const handleClick = async (e) => {
    e.preventDefault();
    if (busy) return;
    // Unauthenticated → send to login carrying the lab_code; the login
    // page will finish the SSO handoff after the user signs in.
    if (!user) {
      navigate(`/login?sso_lab=${encodeURIComponent(labCode)}`);
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/sso/issue-handoff", { lab_code: labCode });
      // Small perceived delay so the click feels intentional rather
      // than a jarring instant page-swap.
      setTimeout(() => { window.location.href = data.redirect_url; }, HANDOFF_DELAY_MS);
    } catch (err) {
      setBusy(false);
      // Fall back to the bare partner URL without SSO — student will
      // hit the partner's own login page, still functional.
      window.location.href = extUrl;
    }
  };

  return (
    <a
      href={extUrl}
      className={className}
      data-testid={testid}
      aria-busy={busy || undefined}
      {...(externalTestidAttr ? { "data-external-url": extUrl } : {})}
      onClick={handleClick}
    >
      {children}
    </a>
  );
}
